import { isClerkAPIResponseError, useAuth } from "@clerk/clerk-expo";
import { useCallback, useEffect, useRef } from "react";

import { captureException } from "@/sentry";

export default function useGetAuthToken() {
  // Clerk needs to be initialized before this hook can be used
  const { getToken, isLoaded, isSignedIn } = useAuth();
  // Unfortunately, all of the useAuth hook's return values are unstable, so we
  // need to use refs to avoid downstream consumers re-rendering incessantly
  const getTokenRef = useRef(getToken);
  const isSignedInRef = useRef(isSignedIn);
  const isLoadedRef = useRef(isLoaded);

  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  useEffect(() => {
    isSignedInRef.current = isSignedIn;
  }, [isSignedIn]);

  useEffect(() => {
    isLoadedRef.current = isLoaded;
  }, [isLoaded]);

  const getAccessToken = useCallback(async () => {
    if (!isLoadedRef.current || !isSignedInRef.current) {
      return null;
    }
    try {
      const token = await getTokenRef.current();
      return token;
    } catch (error) {
      // An expired or revoked session surfaces here as a Clerk
      // `authentication_invalid` error even when local state still reports the
      // user as signed in. It's expected and handled (we return null and let
      // the app re-authenticate), so don't report it to Sentry as noise.
      const isInactiveSession =
        isClerkAPIResponseError(error) &&
        error.errors.some((e) => e.code === "authentication_invalid");
      if (!isInactiveSession) {
        captureException(error, {
          level: "warning",
          tags: { category: "auth" },
        });
      }
      return null;
    }
  }, []);

  return getAccessToken;
}
